import React, { useEffect, useRef, useState } from "react";
import type { CardDetailsAttachment, ScannedCardDetails } from "../types";
import type { CardIssue } from "../../../../features/card-to-crm/types/cardToCrm.types";

type DetailField = Exclude<keyof ScannedCardDetails, "issues">;

interface CardDetailsReviewProps {
  data: CardDetailsAttachment;
  onWriteIntros: (
    contacts: ScannedCardDetails[],
    brief: string,
    captureContext: string
  ) => Promise<void>;
  /** A correction the owner has finished typing. Fired on blur, only when something
   *  actually changed — see `flush` for why it can't wait for the button. */
  onEdited?: (contacts: ScannedCardDetails[], captureContext: string) => void;
  /** SGL-586: re-run the server's field checks (email domain DNS, website, name) on
   *  corrected contacts. One issue list per contact, in order. */
  onRecheck?: (contacts: ScannedCardDetails[]) => Promise<CardIssue[][]>;
}

const FIELDS: { key: DetailField; label: string; type?: string }[] = [
  { key: "name", label: "Name" },
  { key: "title", label: "Title" },
  { key: "company", label: "Company" },
  { key: "email", label: "Email", type: "email" },
  { key: "phone", label: "Phone", type: "tel" },
  // A card that prints only a mobile (common here) extracts into `mobile`, not
  // `phone`. Leaving it off this form showed an empty Phone box beside a card with
  // a number plainly on it — the owner reads that as a failed scan and retypes it,
  // and now the contact carries the same number twice.
  { key: "mobile", label: "Mobile", type: "tel" },
  { key: "website", label: "Website" },
];

/** Full-width rows: the rest pair up two to a line. */
const WIDE: DetailField[] = ["name", "email", "website"];

/** Fields whose edit makes a check result stale. The website is here because the
 *  email's mismatch warning, and its suggestion, are computed from it. */
const CHECKED: DetailField[] = ["name", "email", "website"];

const initials = (name: string | null) =>
  (name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("") || "?";

const inputClass =
  "w-full rounded-lg border border-theme-border bg-theme-surface px-2.5 py-1.5 " +
  "text-[12px] text-theme-fg outline-none focus:border-theme-primary";

const DANGER = "var(--color-danger)";
const WARN_BG = "color-mix(in srgb, var(--color-secondary) 28%, transparent)";

const isBlocking = (issue: CardIssue) => issue.severity === "block";

/**
 * SGL-346: the step between the scan and the drafting call.
 *
 * The extractor is good but not perfect, and the draft leans on every field it is
 * given — a misread company name goes straight into the email, and a wrong address
 * means the intro cannot be sent at all. So the details are correctable BEFORE the
 * draft request goes out, not after, and this card is what triggers that request.
 *
 * SGL-586: it also shows what the server's checks flagged. The extractor misreads
 * long domains a letter at a time ("brownortizcpa.com" -> "brownortizca.com"), and
 * those addresses saved cleanly and bounced weeks later. A domain with no mail or
 * address records, or a card with no name, blocks the intros until it is fixed —
 * this is the last place the fields can be edited before the save card. An email
 * that merely disagrees with the card's website is a warning the owner can keep.
 *
 * `brief` is the owner's own steer ("short, mention the weekly rate"). It rides on
 * the same POST the agent already accepts, so asking for a particular kind of intro
 * costs no extra round trip.
 */
export const CardDetailsReview: React.FC<CardDetailsReviewProps> = ({
  data,
  onWriteIntros,
  onEdited,
  onRecheck,
}) => {
  const [contacts, setContacts] = useState<ScannedCardDetails[]>(data.contacts);
  const [brief, setBrief] = useState(data.brief);
  const [captureContext, setCaptureContext] = useState(data.captureContext);
  const [openIndex, setOpenIndex] = useState<number | null>(() => {
    if (data.contacts.length === 1) return 0;
    const flagged = data.contacts.findIndex((c) => (c.issues ?? []).some(isBlocking));
    return flagged >= 0 ? flagged : null;
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  /** Checks in flight per contact index. A count, not a set: two overlapping checks
   *  of one contact must not let the first to land clear "Checking…" for both. */
  const [checking, setChecking] = useState<Record<number, number>>({});
  const [checkFailed, setCheckFailed] = useState(false);
  /** Warnings the owner chose to keep, by contact index ("the card really says that"). */
  const [kept, setKept] = useState<Record<number, string[]>>({});
  /** Contacts edited since their last check. */
  const stale = useRef<Set<number>>(new Set());
  const latest = useRef(contacts);
  latest.current = contacts;

  const issuesOf = (i: number): CardIssue[] =>
    (contacts[i]?.issues ?? []).filter(
      (x) => isBlocking(x) || !(kept[i] ?? []).includes(x.code)
    );
  const fieldIssue = (i: number, key: DetailField) =>
    issuesOf(i).find((x) => x.field === key);
  const blockingCount = contacts.reduce(
    (n, c) => n + (c.issues ?? []).filter(isBlocking).length,
    0
  );

  const patch = (i: number, key: DetailField, value: string) => {
    if (CHECKED.includes(key)) stale.current.add(i);
    setContacts((prev) =>
      prev.map((c, j) => {
        if (j !== i) return c;
        const next = { ...c, [key]: value.trim() === "" ? null : value };
        // An edited field's old verdict no longer applies. The re-check on blur
        // brings back whatever still does; the save Lambda checks again regardless.
        if (CHECKED.includes(key) && next.issues) {
          next.issues = next.issues.filter(
            (x) => x.field !== key && !(key === "website" && x.field === "email")
          );
        }
        return next;
      })
    );
  };

  const withoutEmail = contacts.filter((c) => !c.email).length;

  /** Trimming on every keystroke would stop you typing a space mid-name, so it
   *  happens here instead — untrimmed, "  Jordan  " lands in the greeting verbatim. */
  const tidy = (c: ScannedCardDetails): ScannedCardDetails => {
    // Fields only. `issues` is this card's own state; a copy handed up to the chat
    // carrying a half-cleared list would skip the re-check when the card remounts.
    const { issues: _issues, ...out } = c;
    FIELDS.forEach(({ key }) => {
      const v = (c[key] ?? "").trim();
      out[key] = v === "" ? null : v;
    });
    return out;
  };

  /** What `onEdited` compares: the fields, never the check results. */
  const snapshot = (people: ScannedCardDetails[], where: string) =>
    JSON.stringify({
      people: people.map(({ issues: _issues, ...fields }) => fields),
      where,
    });

  const persisted = useRef(snapshot(data.contacts.map(tidy), data.captureContext.trim()));

  /** Run the checks for these contacts and put the fresh issues on them. */
  const recheck = async (indices: number[], source?: ScannedCardDetails[]) => {
    if (!onRecheck || !indices.length) return;
    const people = source ?? latest.current;
    const count = (step: number) =>
      setChecking((prev) => {
        const next = { ...prev };
        indices.forEach((i) => {
          next[i] = (next[i] ?? 0) + step;
        });
        return next;
      });
    count(1);
    try {
      const results = await onRecheck(indices.map((i) => tidy(people[i])));
      setCheckFailed(false);
      setContacts((prev) =>
        prev.map((c, j) => {
          const k = indices.indexOf(j);
          // Only if the field hasn't changed again while the check was out.
          if (k < 0 || results[k] === undefined) return c;
          const checkedAs = tidy(people[j]);
          const now = tidy(c);
          const unchanged = CHECKED.every((f) => checkedAs[f] === now[f]);
          return unchanged ? { ...c, issues: results[k] } : c;
        })
      );
    } catch {
      // Not silent: the owner is told, and the save step runs the same checks
      // server-side, so a flagged address still cannot reach the CRM.
      setCheckFailed(true);
    } finally {
      count(-1);
    }
  };

  // A card restored from the thread carries no check results (the conversation
  // stores fields only): check it once when it appears.
  useEffect(() => {
    const unchecked = data.contacts
      .map((c, i) => (c.issues === undefined ? i : -1))
      .filter((i) => i >= 0);
    void recheck(unchecked, data.contacts);
    // Once, for the card as it arrived.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Corrections used to live only in this component's state until "Write the intro"
   * was pressed — but the owner often fixes a field and then asks the agent in the
   * composer instead ("we met at Georgetown, make a greet"). From the second message
   * on, the chat service rebuilds the model's context from the stored scan and
   * discards what the browser holds, so an uncommitted fix means the agent answers
   * about the name it was first given. Live 2026-09-25: the owner retyped the name,
   * asked for a greeting, and got one addressed to the earlier reading.
   *
   * Blur is the moment, not a debounce: reaching the composer blurs whatever field
   * they were in, so the correction is always recorded before the question is asked.
   */
  const flush = () => {
    const edited = Array.from(stale.current);
    stale.current.clear();
    void recheck(edited);
    if (!onEdited) return;
    const next = latest.current.map(tidy);
    const where = captureContext.trim();
    const now = snapshot(next, where);
    if (now === persisted.current) return;
    persisted.current = now;
    onEdited(next, where);
  };

  /** "Use khoa@stmediastudio.com": apply the suggestion and check it straight away. */
  const applySuggestion = (i: number, issue: CardIssue) => {
    if (!issue.suggestion) return;
    patch(i, issue.field, issue.suggestion);
    // patch() is async state; flush on the next tick so it sees the new value.
    setTimeout(flush, 0);
  };

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      await onWriteIntros(contacts.map(tidy), brief.trim(), captureContext.trim());
    } catch (e) {
      setBusy(false);
      setError(
        e instanceof Error ? e.message : "Couldn't start the drafts. Try again in a moment."
      );
    }
    // On success this card is replaced by the drafting state, so `busy` is left on
    // — flipping it back would flash an active button on a card that is going away.
  };

  const isChecking = Object.values(checking).some((n) => n > 0);
  const blocked = blockingCount > 0;

  return (
    <div className="my-1 w-full max-w-[460px] overflow-hidden rounded-2xl border border-theme-border bg-theme-surface">
      <div className="flex items-center gap-2.5 border-b border-theme-border px-4 py-3">
        <span
          className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-xl"
          style={{ background: "color-mix(in srgb, var(--color-secondary) 20%, transparent)" }}
        >
          <span className="material-icons-outlined text-[18px] text-theme-fg-muted">badge</span>
        </span>
        <div className="min-w-0">
          <p className="truncate text-[13px] font-bold text-theme-fg">
            {contacts.length} card{contacts.length === 1 ? "" : "s"} read
          </p>
          {blocked ? (
            <p className="truncate text-[11px]" style={{ color: DANGER }}>
              {blockingCount} field{blockingCount === 1 ? " needs" : "s need"} a look
              before I write the intro{contacts.length === 1 ? "" : "s"}.
            </p>
          ) : (
            <p className="truncate text-[11px] text-theme-fg-muted">
              Fix anything the scan misread, then I'll write the intro
              {contacts.length === 1 ? "" : "s"}.
            </p>
          )}
        </div>
      </div>

      <ul className="divide-y divide-theme-border">
        {contacts.map((c, i) => {
          const isOpen = openIndex === i;
          const issues = issuesOf(i);
          const headline = issues.find(isBlocking) ?? issues[0];
          const nameIssue = fieldIssue(i, "name");
          return (
            <li key={i}>
              <button
                type="button"
                onClick={() => setOpenIndex(isOpen ? null : i)}
                aria-expanded={isOpen}
                className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left transition-colors hover:bg-theme-background"
              >
                <span
                  className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-[11px] font-bold"
                  style={
                    headline && isBlocking(headline)
                      ? {
                          background: "color-mix(in srgb, var(--color-danger) 14%, transparent)",
                          color: DANGER,
                        }
                      : {
                          background: "color-mix(in srgb, var(--color-primary) 16%, transparent)",
                          color: "var(--color-primary)",
                        }
                  }
                >
                  {headline && isBlocking(headline) ? "!" : initials(c.name)}
                </span>
                <span className="min-w-0 flex-1">
                  <span
                    className="block truncate text-[13px] font-semibold text-theme-fg"
                    style={nameIssue ? { color: DANGER } : undefined}
                  >
                    {c.name || "Name not read — add it"}
                  </span>
                  <span className="block truncate text-[11px] text-theme-fg-muted">
                    {c.email || "No email read — add one to send an intro"}
                  </span>
                  {headline && headline.field === "email" && (
                    <span
                      className="mt-0.5 block truncate text-[11px] font-semibold"
                      style={isBlocking(headline) ? { color: DANGER } : undefined}
                    >
                      {headline.message}
                    </span>
                  )}
                </span>
                <span className="material-icons-outlined flex-shrink-0 text-[18px] text-theme-fg-muted">
                  {isOpen ? "expand_less" : "edit"}
                </span>
              </button>

              {isOpen && (
                <div className="grid grid-cols-2 gap-2 bg-theme-background px-4 pb-3.5 pt-1">
                  {FIELDS.map((f) => {
                    const issue = fieldIssue(i, f.key);
                    const block = issue && isBlocking(issue);
                    return (
                      <label
                        key={f.key}
                        className={WIDE.includes(f.key) ? "col-span-2" : undefined}
                      >
                        <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-theme-fg-muted">
                          {f.label}
                        </span>
                        <input
                          type={f.type || "text"}
                          value={c[f.key] ?? ""}
                          onChange={(e) => patch(i, f.key, e.target.value)}
                          onBlur={flush}
                          aria-invalid={block ? true : undefined}
                          className={inputClass}
                          // The ring, not just the border: dark mode forces every input's
                          // border-color with !important (ThemeManager), which hid the flag.
                          style={
                            block
                              ? { borderColor: DANGER, boxShadow: `0 0 0 1px ${DANGER}` }
                              : issue
                                ? {
                                    borderColor: "var(--color-secondary)",
                                    boxShadow: "0 0 0 1px var(--color-secondary)",
                                  }
                                : undefined
                          }
                        />
                        {issue && (
                          <div
                            role={block ? "alert" : undefined}
                            className="mt-1 rounded-md px-2 py-1 text-[11px] leading-snug"
                            style={
                              block
                                ? {
                                    color: DANGER,
                                    background:
                                      "color-mix(in srgb, var(--color-danger) 8%, transparent)",
                                  }
                                : { background: WARN_BG }
                            }
                          >
                            <span className={block ? undefined : "text-theme-fg"}>
                              {issue.message}
                            </span>
                            {(issue.suggestion || !block) && (
                              <span className="mt-1 flex flex-wrap gap-x-3 gap-y-1">
                                {issue.suggestion && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      applySuggestion(i, issue);
                                    }}
                                    // Full-contrast text: the primary green and the muted grey
                                    // both read faintly on these tinted boxes in one theme or the other.
                                    className="font-semibold text-theme-fg underline"
                                  >
                                    Use {issue.suggestion}
                                  </button>
                                )}
                                {!block && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      setKept((prev) => ({
                                        ...prev,
                                        [i]: [...(prev[i] ?? []), issue.code],
                                      }));
                                    }}
                                    className="font-semibold text-theme-fg underline"
                                  >
                                    Keep as printed
                                  </button>
                                )}
                              </span>
                            )}
                          </div>
                        )}
                      </label>
                    );
                  })}
                  {(checking[i] ?? 0) > 0 && (
                    <p className="col-span-2 text-[11px] text-theme-fg-muted">Checking…</p>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>

      <div className="space-y-2.5 border-t border-theme-border px-4 py-3">
        <label className="block">
          <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-theme-fg-muted">
            Where you met (optional)
          </span>
          <input
            type="text"
            value={captureContext}
            onChange={(e) => setCaptureContext(e.target.value)}
            onBlur={flush}
            placeholder="Georgetown luncheon"
            className={inputClass}
          />
        </label>

        <label className="block">
          <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-theme-fg-muted">
            What should the intro say? (optional)
          </span>
          <textarea
            value={brief}
            onChange={(e) => setBrief(e.target.value)}
            rows={2}
            placeholder="Keep it short and mention the weekly rate"
            className={`${inputClass} resize-none`}
          />
        </label>

        {withoutEmail > 0 && (
          <p className="text-[11px] text-theme-fg-muted">
            {withoutEmail === contacts.length
              ? "No email addresses yet — I'll still write the intros, but none can be sent."
              : `${withoutEmail} of these has no email, so its intro can't be sent.`}
          </p>
        )}

        {checkFailed && (
          <p className="text-[11px] text-theme-fg-muted">
            Couldn't re-check that change just now — it is checked again when you save.
          </p>
        )}

        {error && (
          <p className="text-[11px]" style={{ color: DANGER }}>
            {error}
          </p>
        )}

        <button
          type="button"
          onClick={submit}
          disabled={busy || blocked || isChecking}
          className="w-full rounded-xl bg-theme-primary px-4 py-2 text-[13px] font-semibold text-white transition-opacity disabled:opacity-60"
        >
          {busy
            ? "Writing…"
            : isChecking
              ? "Checking…"
              : blocked
                ? `Fix the ${blockingCount} flagged field${blockingCount === 1 ? "" : "s"} first`
                : `Write the intro${contacts.length === 1 ? "" : `s (${contacts.length})`}`}
        </button>
      </div>
    </div>
  );
};

export default CardDetailsReview;
